import { useCallback, useEffect, useMemo, useState } from "react";
import getDecryptedToken from "../../api/getDecryptedToken";
import baseUrl from "../../api/baseUrl";
import type { ClinicData } from "./clinic/ClinicTabPanel";
import type { StorageUsage } from "./storage/StorageTabPanel";
import type { SmsBalanceData, SmsUsageStats } from "./sms/SmsTabPanel";
import type { EmployeeProfile, PaymentData } from "./accountTypes";

export function useMyAccountData() {
  const [clinic, setClinic] = useState<ClinicData | null>(null);
  const [payment, setPayment] = useState<PaymentData | null>(null);
  const [storage, setStorage] = useState<StorageUsage | null>(null);
  const [storageError, setStorageError] = useState<string | null>(null);
  const [storageLoading, setStorageLoading] = useState(true);
  const [smsBalance, setSmsBalance] = useState<SmsBalanceData | null>(null);
  const [smsUsage, setSmsUsage] = useState<SmsUsageStats | null>(null);
  const [smsError, setSmsError] = useState<string | null>(null);
  const [smsLoading, setSmsLoading] = useState(true);
  const [employee, setEmployee] = useState<EmployeeProfile | null>(null);
  const [userLoading, setUserLoading] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const eid = getDecryptedToken("eid");
  const userName = getDecryptedToken("userName");
  const userRole = getDecryptedToken("role");
  const clinicNameToken = getDecryptedToken("clinicName");
  const clinicPhone = getDecryptedToken("clinicPhoneNumber");
  const userAddress = getDecryptedToken("address");
  const clinicLogo = getDecryptedToken("clinicLogo");
  const showLogo = getDecryptedToken("logo") === "true";

  useEffect(() => {
    document.title = clinicNameToken
      ? `${clinicNameToken} | My Account`
      : "My Account";
  }, [clinicNameToken]);

  const loadStorage = useCallback(() => {
    setStorageLoading(true);
    setStorageError(null);
    return baseUrl
      .get("/client/account/aws-storage")
      .then((res) => {
        if (res?.data?.success) {
          setStorage(res.data.data as StorageUsage);
        } else {
          setStorage(null);
          setStorageError("Could not load storage usage.");
        }
      })
      .catch(() => {
        setStorage(null);
        setStorageError("Could not load storage usage.");
      })
      .finally(() => setStorageLoading(false));
  }, []);

  const loadClinic = useCallback(() => {
    return baseUrl
      .get("/clinic/getBillData")
      .then((res) => {
        setClinic(res?.data?.data ?? null);
        return res?.data?.data ?? null;
      })
      .catch(() => {
        setError("Could not load account details. Please try again.");
        return null;
      });
  }, []);

  const loadSms = useCallback((refreshBalance = false) => {
    setSmsLoading(true);
    setSmsError(null);
    return Promise.all([
      baseUrl.get("/client/account/sms-balance", {
        params: { refresh: refreshBalance ? "true" : "false" },
      }),
      baseUrl.get("/client/account/sms-usage-stats"),
    ])
      .then(([balanceRes, statsRes]) => {
        if (balanceRes?.data?.success) {
          setSmsBalance(balanceRes.data.data as SmsBalanceData);
          setSmsError(null);
        } else {
          setSmsBalance(null);
          setSmsError("Could not load SMS balance.");
        }

        if (statsRes?.data?.success) {
          setSmsUsage(statsRes.data.data?.usage ?? null);
        }
      })
      .catch(() => {
        setSmsBalance(null);
        setSmsUsage(null);
        setSmsError("Could not load SMS usage.");
      })
      .finally(() => setSmsLoading(false));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setStorageLoading(true);
    setSmsLoading(true);

    const employeeReq = eid
      ? baseUrl.get("/employee/findEmployeeById", { headers: { employeeid: eid } })
      : Promise.resolve(null);

    Promise.all([
      baseUrl.get("/clinic/getBillData"),
      baseUrl.get("/client/fee/getClinicPaymentbyId").catch(() => null),
      baseUrl.get("/client/account/aws-storage").catch(() => null),
      baseUrl
        .get("/client/account/sms-balance", { params: { refresh: "false" } })
        .catch((err) => ({ data: { success: false }, status: err?.response?.status })),
      baseUrl.get("/client/account/sms-usage-stats").catch(() => null),
      employeeReq.catch(() => null),
    ])
      .then(([clinicRes, paymentRes, storageRes, smsBalanceRes, smsStatsRes, employeeRes]) => {
        if (cancelled) return;
        setClinic(clinicRes?.data?.data ?? null);
        if (employeeRes?.data?.success) {
          setEmployee(employeeRes.data.data as EmployeeProfile);
        } else {
          setEmployee(null);
        }

        const payPayload = paymentRes?.data?.data;
        const pkg = payPayload?.result;
        setPayment(
          pkg
            ? {
                price: payPayload?.price ?? pkg?.price,
                paymentendDate:
                  payPayload?.paymentendDate ?? pkg?.paymentendDate,
                packageName: pkg?.packageName,
                startDate: pkg?.startDate,
                endDate: pkg?.endDate,
                free: pkg?.free,
                active: pkg?.active,
                recurring: pkg?.recurring,
                payhereOrderId: pkg?.payhereOrderId,
                lastPayhereNotifyAt: pkg?.lastPayhereNotifyAt,
                recurringInstallPaid: pkg?.recurringInstallPaid,
              }
            : payPayload
              ? {
                  price: payPayload.price,
                  paymentendDate: payPayload.paymentendDate,
                }
              : null
        );

        if (storageRes?.data?.success) {
          setStorage(storageRes.data.data as StorageUsage);
          setStorageError(null);
        } else {
          setStorage(null);
          setStorageError("Could not load storage usage.");
        }

        if (smsBalanceRes?.data?.success) {
          setSmsBalance(smsBalanceRes.data.data as SmsBalanceData);
          setSmsError(null);
        } else if (smsBalanceRes?.status === 404) {
          setSmsBalance(null);
          setSmsError(
            "SMS usage API is missing on the server. Deploy the latest backend (client/account/sms-* routes) or use localhost API in development."
          );
        } else if (smsBalanceRes) {
          setSmsBalance(null);
          setSmsError("Could not load SMS usage.");
        } else {
          setSmsBalance(null);
          setSmsError(null);
        }

        if (smsStatsRes?.data?.success) {
          setSmsUsage(smsStatsRes.data.data?.usage ?? null);
        } else {
          setSmsUsage(null);
        }
      })
      .catch(() => {
        if (!cancelled) setError("Could not load account details. Please try again.");
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
          setStorageLoading(false);
          setSmsLoading(false);
          setUserLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [eid]);

  const logoUrl = useMemo(() => {
    const resolveLogoSrc = (value?: string | null) => {
      const key = typeof value === "string" ? value.trim() : "";
      if (!key) return null;
      // Backend AWS signed URL or any absolute URL
      if (/^https?:\/\//i.test(key)) return key;
      // Legacy local uploads path
      if (key.includes("/uploads/images")) {
        const api = process.env.REACT_APP_API ?? "";
        return `${api}${key}`;
      }
      // Raw S3 object key — browser cannot load without a signed URL
      return null;
    };

    return (
      resolveLogoSrc(clinic?.logoimage) ||
      (showLogo ? resolveLogoSrc(clinicLogo) : null)
    );
  }, [clinic?.logoimage, clinicLogo, showLogo]);

  return {
    clinic,
    payment,
    storage,
    storageError,
    storageLoading,
    smsBalance,
    smsUsage,
    smsError,
    smsLoading,
    employee,
    userLoading,
    loading,
    error,
    loadStorage,
    loadClinic,
    loadSms,
    logoUrl,
    userName,
    userRole,
    clinicNameToken,
    clinicPhone,
    userAddress,
  };
}
